import type { ReactNode } from "react";
import { HORIZON_LABELS, type AssetHazardResult } from "@/lib/results";
import { getResultsForHorizon, orderedHorizons } from "@/lib/results-filters";
import type { ClimateScenario, TimeHorizon } from "@shared/scoring/types";

interface HorizonComparisonGridProps {
  horizons: TimeHorizon[];
  scenario: ClimateScenario;
  results: AssetHazardResult[];
  children: (resultsForHorizon: AssetHazardResult[], horizon: TimeHorizon) => ReactNode;
}

/**
 * Renders one copy of `children` per active horizon, side by side, each fed
 * only that horizon's slice of `results`. The wrapped component (a takeaway
 * card set, a chart, a panel) needs no awareness horizons exist at all — it
 * receives a plain, already-correctly-sliced `AssetHazardResult[]` exactly
 * like it always has.
 *
 * Flexbox with wrap, not a fixed-column grid: a rigid `grid-cols-3` squeezes
 * every column to a third of the row width no matter how narrow the
 * viewport gets, which broke on phones (and looked cramped with all 3
 * horizons active even on desktop, since these panels have their own
 * internal charts/tables that need real width). `min-w` lets each column
 * wrap onto its own line once there's no room for it at a usable width,
 * instead of shrinking indefinitely.
 */
export function HorizonComparisonGrid({ horizons, scenario, results, children }: HorizonComparisonGridProps) {
  const active = orderedHorizons(horizons);
  if (active.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-4">
      {active.map((horizon) => (
        <div key={horizon} className="flex min-w-[280px] flex-1 flex-col gap-2">
          {active.length > 1 && (
            <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-400">{HORIZON_LABELS[horizon]}</p>
          )}
          {children(getResultsForHorizon(results, horizon, scenario), horizon)}
        </div>
      ))}
    </div>
  );
}
